<template>
  <div id="active-plans">
    <b-row class="mt-4" v-if="loadingData && !loadError">
      <b-col
        v-for="(item, index) in 3"
        cols="12"
        md="6"
        lg="4"
        :key="item + index"
        class="mb-4"
      >
        <b-progress
          variant="primary"
          :max="100"
          class="w-100"
          show-progress
          animated
          height="200px"
        >
          <b-progress-bar :value="100">Loading...</b-progress-bar>
        </b-progress>
      </b-col>
    </b-row>
    <b-row v-else-if="loadError" class="mt-4" align-v="stretch">
      <b-col cols="12" class="mb-4">
        <div class="text-center" title="Could not load, click on retry">
          <b-button
            size="sm"
            @click="loadInvestments()"
            variant="light"
            class="w-100"
            style="height:200px"
          >
            <bad-network-icon />
            <br />Error Loading Data, <br />click to retry
            <reload-icon class="ml-1" />
          </b-button>
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-4" v-else align-v="stretch">
      <b-col
        cols="12"
        md="6"
        lg="4"
        class="mb-4"
        v-for="(investment, index) in investments"
        :key="investment.amount + index"
      >
        <b-button
          @click="handleClicked(investment)"
          variant="white"
          class="w-100 wrapper h-100 bg-white dash-card investment-blocks text-left p-0"
        >
          <div
            class="upper mb-1 bg-light p-4 text-white shadow-2xl"
            :style="{
              backgroundImage:
                ` linear-gradient(0deg, rgba(0,0,0,0.6), transparent), url(` +
                investment.package.imageurl +
                `)`
            }"
          >
            <b-row align-h="between">
              <b-col cols="auto">
                <h5>
                  {{ investment.package.packagename }} <br />
                  <div style="font-size:15px">
                    {{
                      parseFloat(investment.package.roi) +
                        "% in " +
                        investment.package.duration +
                        "months"
                    }}
                  </div>
                </h5>
              </b-col>
              <b-col cols="auto" class="text-right">
                <i
                  v-if="investment.status == 'active'"
                  class="fa fa-check-circle fa-lg text-primary"
                  aria-hidden="true"
                ></i>
                <component
                  class="text-white"
                  :is="`${investment.type}-icon`"
                  :size="40"
                ></component>
              </b-col>
            </b-row>
          </div>
          <div class="upper p-4">
            <h4 class="mb-0 lft">
              <Money
                class="text-primary mb-0"
                :value="investment.amount || 0"
              />
            </h4>
            <small class="text-dark rgt" style="margin-top: 5px"
              >{{
                parseInt(investment.amount) / investment.package.amount
              }}
              Unit(s)</small
            >
            <div class="clr"></div>
            <b-row style="margin-top: 10px">
              <b-col
                class="wrap"
                :style="{ opacity: investment.type == 'crowd-funding' ? 1 : 0 }"
              >
                <small>Growth:</small>
                <span class="d-block text-orange">{{
                  formatMoney(
                    investment.growthSoFar + Number(investment.amount)
                  )
                }}</span>
              </b-col>
              <b-col
                class="wrap"
                :style="{ opacity: investment.type == 'crowd-funding' ? 1 : 0 }"
                align="right"
              >
                <small>Returns:</small>
                <span class="d-block text-orange">{{
                  formatMoney(
                    Number(
                      (investment.amount * investment.roi) / 100 +
                        Number(investment.amount)
                    )
                  )
                }}</span>
              </b-col>
            </b-row>
            <b-progress
              class="mb-3 progress-bar-striped"
              :value="investment.daysSoFar"
              :max="investment.totalDays"
              height="7px"
              :style="{ opacity: investment.type != 'crowd-funding' ? 0 : 1 }"
            ></b-progress>

            <b-row align-h="between">
              <b-col>
                <small class="d-block text-black-50">Acquired:</small>
                <small class="d-investment text-dark">
                  {{
                    LuxonDateTime.fromJSDate(
                      new Date(investment.start)
                    ).toFormat("LLL dd yyyy")
                  }}
                </small>
              </b-col>
              <b-col
                class="text-right"
                v-if="investment.type == 'crowd-funding'"
              >
                <small class="d-block text-black-50">Matures:</small>
                <small class="d-investment text-dark">
                  {{
                    LuxonDateTime.fromJSDate(
                      new Date(investment.maturity)
                    ).toFormat("LLL dd yyyy")
                  }}
                </small>
              </b-col>
              <b-col v-else>
                <div class="wrap position-absolute bottom-0 right-0 mr-3">
                  <b-badge variant="primary">Co-Ownership</b-badge>
                </div>
              </b-col>
            </b-row>
          </div>
        </b-button>
      </b-col>
    </b-row>
    <b-row v-if="investments.length == 0">
      <b-col cols="12">
        <div
          class="d-flex bg-white dash-card p-5 align-items-center justify-content-center"
          style="height:200px"
        >
          <div class="wrap text-center">
            <h5>No Active Investments</h5>
            <b-button variant="primary" @click="$emit('setInvestTab')"
              >Fund a Farm</b-button
            >
          </div>
        </div>
      </b-col>
    </b-row>
    <InvestmentDetailsModal :investment="activeInvestment" />
  </div>
</template>

<script>
import { DateTime as LuxonDateTime } from "luxon";
import { UserService } from "@/services";
import { formatMoney } from "@/utils/helpers";
import loadData from "@/mixins/loadData";
import InvestmentDetailsModal from "./InvestmentDetailsModal";

export default {
  data() {
    return {
      investments: {},
      activeInvestment: {},
      investmentData: {},
      LuxonDateTime: LuxonDateTime
    };
  },
  mixins: [loadData],
  methods: {
    formatMoney,
    async loadInvestments() {
      this.loadingData = true;
      this.loadError = false;
      try {
        const { data } = await UserService.getInvestments();
        this.investments = data.investments.data;
        this.investmentData = data.investments;
        // this.activeInvestment = data[0] || {};
      } catch (err) {
        this.loadError = true;
      } finally {
        this.loadingData = false;
      }
    },
    handleClicked(investment) {
      this.activeInvestment = investment;
      this.$bvModal.show("investment-details-modal");
    }
  },
  components: {
    InvestmentDetailsModal
  },
  mounted() {
    this.loadInvestments();
  }
};
</script>

<style>
.upper {
  background-size: cover;
  border-radius: 5px 5px 0 0;
}

.wrap small {
  font-size: 12px;
  font-weight: 700;
}

.wrap .d-block {
  font-size: 15px;
  font-weight: 700;
}
</style>
